import { MetadataFieldType } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { useListAllMetadataFields } from '@/hooks/api/metadata/useListAllMetadataFields';
import { createUseStyles } from '@/services/createUseStyles';
import { Alert, Box, Button, Chip } from '@mui/material';
import { BulkUploadData, MetadataFieldDefinition } from '../../types';
import { createDataPreviewStepColumns } from '../columnDefinitions';
import { MetadataBulkDataGrid } from '../MetadataBulkDataGrid';

const useStyles = createUseStyles(theme => ({
  DataPreviewStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  DataPreviewStep_content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '24px',
  },
  DataPreviewStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
    flexShrink: 0,
  },
  DataPreviewStep_warnings: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '16px',
    flexShrink: 0,
  },
  DataPreviewStep_dataGridContainer: {
    flex: 1,
    overflow: 'hidden',
    marginBottom: '24px',
  },
  DataPreviewStep_actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    flexShrink: 0,
  },
  DataPreviewStep_metadataFields: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
}));

export const DataPreviewStep = ({
  data,
  onNext,
  onBack,
  onDataChange,
}: {
  data?: BulkUploadData;
  onNext: () => void;
  onBack: () => void;
  onDataChange: (data: BulkUploadData) => void;
}) => {
  const c = useStyles();
  const { metadataFields: existingMetadataFields, isLoading: isLoadingMetadataFields } = useListAllMetadataFields();

  if (!data) {
    return <Alert severity='error'>No data available. Please go back and upload a file.</Alert>;
  }

  const handleNext = () => {
    if (!isLoadingMetadataFields && existingMetadataFields) {
      const existingFieldsMap = new Map(
        existingMetadataFields.map(field => [field.name.toLowerCase(), { name: field.name, type: field.type }])
      );

      const updatedDefinitions: MetadataFieldDefinition[] = data.metadataFields.map(fieldName => {
        const lowerFieldName = fieldName.toLowerCase();
        const existingField = existingFieldsMap.get(lowerFieldName);
        const isExisting = !!existingField;
        const fieldType = existingField?.type || MetadataFieldType.Text;

        return {
          name: fieldName,
          type: fieldType,
          isNew: !isExisting,
        };
      });

      const updatedData = {
        ...data,
        metadataFieldDefinitions: updatedDefinitions,
      };
      onDataChange(updatedData);
    }

    onNext();
  };

  const { parsedData, metadataFields, file } = data;

  const columns = createDataPreviewStepColumns(metadataFields);

  const rows = parsedData.slice(0, 10).map((row, index) => ({
    id: `preview-${index}`,
    assetName: row.assetName,
    metadata: row.metadata,
  }));

  return (
    <Box className={c.DataPreviewStep_root}>
      <Box className={c.DataPreviewStep_content}>
        <HeaderText gutterBottom>Preview Your Data</HeaderText>
        <Box className={c.DataPreviewStep_summary}>
          <HeaderText gutterBottom>File Summary</HeaderText>
          <BodyText>
            <strong>File:</strong> {file.name}
          </BodyText>
          <BodyText>
            <strong>Total Assets:</strong> {parsedData.length}
          </BodyText>
          <BodyText>
            <strong>Metadata Fields:</strong>
          </BodyText>
          <Box className={c.DataPreviewStep_metadataFields}>
            {metadataFields.map(field => (
              <Chip key={field} label={field} size='small' />
            ))}
          </Box>
        </Box>
        <Box className={c.DataPreviewStep_warnings}>
          {data.duplicateWarnings && data.duplicateWarnings.length > 0 && (
            <Alert severity='warning' sx={{ marginBottom: 2 }}>
              <HeaderText gutterBottom>Duplicate Asset Names Detected</HeaderText>
              <BodyText sx={{ marginBottom: 1 }}>
                The following assets appear multiple times. Only the first occurrence will be used:
              </BodyText>
              {data.duplicateWarnings.map((warning, index) => (
                <Box key={`${warning.assetName}-${index}`} sx={{ marginBottom: 1 }}>
                  <BodyText component='div'>
                    <strong>&quot;{warning.assetName}&quot;</strong> - Using row {warning.firstRowIndex}, ignoring row
                    {warning.duplicateRowIndexes.length === 1 ? '' : 's'} {warning.duplicateRowIndexes.join(', ')}
                  </BodyText>
                </Box>
              ))}
            </Alert>
          )}
          {data.duplicateMetadataColumnWarnings && data.duplicateMetadataColumnWarnings.length > 0 && (
            <Alert severity='warning' sx={{ marginBottom: 2 }}>
              <HeaderText gutterBottom>Duplicate Metadata Columns Detected</HeaderText>
              <BodyText sx={{ marginBottom: 1 }}>
                The following metadata columns appear multiple times. Only the first occurrence will be used:
              </BodyText>
              {data.duplicateMetadataColumnWarnings.map((warning, index) => (
                <Box key={`${warning.fieldName}-${index}`} sx={{ marginBottom: 1 }}>
                  <BodyText component='div'>
                    <strong>&quot;{warning.fieldName}&quot;</strong> - Using column {warning.firstColumnIndex}, ignoring
                    column
                    {warning.duplicateColumnIndexes.length === 1 ? '' : 's'} {warning.duplicateColumnIndexes.join(', ')}
                  </BodyText>
                </Box>
              ))}
            </Alert>
          )}
        </Box>
        <HeaderText gutterBottom>Data Preview (showing first 10 rows)</HeaderText>
        <Box className={c.DataPreviewStep_dataGridContainer}>
          <MetadataBulkDataGrid columns={columns} rows={rows} rowCount={rows.length} hideFooter={true} />
        </Box>
        {parsedData.length > 10 && (
          <BodyText color='textSecondary' sx={{ marginBottom: 2 }}>
            ... and {parsedData.length - 10} more rows
          </BodyText>
        )}
      </Box>
      <Box className={c.DataPreviewStep_actions}>
        <Button variant='outlined' onClick={onBack}>
          Back
        </Button>
        <Button variant='contained' onClick={handleNext}>
          Continue
        </Button>
      </Box>
    </Box>
  );
};
